if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      if (!this.form) return;

      const idInput = this.form.querySelector('[name="id"]');
      if (idInput) idInput.disabled = false;

      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.nativeCart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.submitButton = this.querySelector('[type="submit"]');

      if (this.nativeCart && this.submitButton) {
        this.submitButton.setAttribute('aria-haspopup', 'dialog');
      }

      this.hideErrors = this.dataset.hideErrors === 'true';
      this.error = false;
    }

    rootUrl() {
      return window.Shopify?.routes?.root || '/';
    }

    cartUrl() {
      return window.routes?.cart_url || `${this.rootUrl()}cart`;
    }

    kachingTargets() {
      return Array.from(new Set([
        ...document.querySelectorAll(
          'kaching-cart-drawer, #kaching-cart, [data-kaching-cart-drawer]'
        )
      ]));
    }

    kachingApis() {
      return [
        window.KachingCart,
        window.kachingCart,
        window.Kaching?.cart
      ].filter(Boolean);
    }

    prefersKaching() {
      return (
        window.__AQ_CART_OWNER__ === 'kaching' ||
        this.kachingTargets().length > 0
      );
    }

    callOpenMethod(target) {
      for (const method of ['open', 'show', 'showDrawer']) {
        if (typeof target?.[method] !== 'function') continue;

        try {
          target[method]();
          return true;
        } catch (error) {
          console.warn('ARMATEQ could not open the Kaching cart drawer.', error);
        }
      }

      return false;
    }

    dispatchKaching(name, detail) {
      document.dispatchEvent(new CustomEvent(name, {
        bubbles: true,
        detail
      }));
      window.dispatchEvent(new CustomEvent(name, { detail }));
    }

    signalKaching(cart, addResult) {
      const detail = {
        cart,
        addResult,
        source: 'armateq-product-form'
      };

      [
        'cart:refresh',
        'cart:updated',
        'kaching:cart:refresh',
        'kaching-cart:refresh'
      ].forEach((name) => this.dispatchKaching(name, detail));

      this.kachingApis().forEach((api) => this.callOpenMethod(api));
      this.kachingTargets().forEach((drawer) => this.callOpenMethod(drawer));

      [
        'kaching:cart:open',
        'kaching-cart:open'
      ].forEach((name) => this.dispatchKaching(name, detail));
    }

    async fetchCart() {
      try {
        const response = await fetch(`${this.rootUrl()}cart.js`, {
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        return response.ok ? response.json() : null;
      } catch (error) {
        return null;
      }
    }

    refreshCartCount(cart) {
      if (!cart) return;
      const count = String(cart.item_count || 0);

      document.querySelectorAll(
        '[data-cart-count], .aq-header__cart-count, .cart-count-bubble span[aria-hidden="true"]'
      ).forEach((node) => {
        node.textContent = count;
      });

      const cartLink = document.querySelector('#cart-icon-bubble');
      if (cartLink) {
        const currentLabel = cartLink.getAttribute('aria-label') || 'Cart';
        const baseLabel = currentLabel.replace(/\s*\(\d+\)\s*$/, '');
        cartLink.setAttribute('aria-label', `${baseLabel} (${count})`);
      }
    }

    async handoffToKaching(addResult) {
      const cart = await this.fetchCart();
      this.refreshCartCount(cart);
      this.signalKaching(cart, addResult);

      // Kaching can mount or animate later inside Shopify's editor and Meta's
      // embedded browsers. Retry without ever changing the current page.
      [220, 700, 1500].forEach((delay) => {
        window.setTimeout(
          () => this.signalKaching(cart, addResult),
          delay
        );
      });

      return true;
    }

    nativeSectionIds() {
      if (!this.nativeCart?.getSectionsToRender) return [];

      try {
        return this.nativeCart
          .getSectionsToRender()
          .map((section) => section.id)
          .filter(Boolean);
      } catch (error) {
        return [];
      }
    }

    renderNativeCart(response) {
      if (this.nativeCart?.renderContents && response?.sections) {
        try {
          this.nativeCart.renderContents(response);
          return true;
        } catch (error) {
          console.warn('ARMATEQ could not render the native cart drawer.', error);
        }
      }

      // This fallback applies only when Kaching is not the selected cart.
      window.location.assign(this.cartUrl());
      return false;
    }

    emitState(state, message = '') {
      this.dispatchEvent(new CustomEvent('aq:product-form-state', {
        bubbles: true,
        detail: { state, message }
      }));
    }

    publishCartEvent(eventName, payload) {
      if (
        typeof window.publish === 'function' &&
        window.PUB_SUB_EVENTS?.[eventName]
      ) {
        window.publish(window.PUB_SUB_EVENTS[eventName], payload);
      } else if (
        typeof publish === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined' &&
        PUB_SUB_EVENTS?.[eventName]
      ) {
        publish(PUB_SUB_EVENTS[eventName], payload);
      }
    }

    async onSubmitHandler(event) {
      event.preventDefault();

      if (
        !this.submitButton ||
        this.submitButton.getAttribute('aria-disabled') === 'true'
      ) return;

      this.error = false;
      this.handleErrorMessage();
      this.submitButton.setAttribute('aria-disabled', 'true');
      this.submitButton.classList.add('loading');
      this.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');
      this.emitState('adding');

      const formData = new FormData(this.form);
      formData.delete('return_to');
      formData.delete('checkout');

      const useKaching = this.prefersKaching();

      if (this.nativeCart && !useKaching) {
        const sections = this.nativeSectionIds();

        if (sections.length) {
          formData.set('sections', sections.join(','));
          formData.set('sections_url', window.location.pathname);
          this.nativeCart.setActiveElement?.(document.activeElement);
        }
      }

      try {
        const response = await fetch(
          window.routes?.cart_add_url || `${this.rootUrl()}cart/add.js`,
          {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: formData
          }
        );

        let result = {};
        try {
          result = await response.json();
        } catch (error) {
          result = {};
        }

        if (!response.ok || result.status) {
          const message =
            result.description ||
            result.message ||
            'Unable to add this item to your cart.';

          this.publishCartEvent('cartError', {
            source: 'product-form',
            productVariantId: formData.get('id'),
            errors: message,
            message: result.message
          });

          this.handleErrorMessage(message);

          const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
          if (soldOutMessage) {
            this.submitButton.setAttribute('aria-disabled', 'true');
            this.submitButton.querySelector('span')?.classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
          }

          this.error = true;
          this.emitState('error', message);
          return;
        }

        this.publishCartEvent('cartUpdate', {
          source: 'product-form',
          productVariantId: formData.get('id')
        });

        const completeHandoff = async () => {
          if (useKaching) {
            await this.handoffToKaching(result);
          } else {
            this.renderNativeCart(result);
          }
        };

        const quickAddModal = this.closest('quick-add-modal');
        if (quickAddModal) {
          document.body.addEventListener('modalClosed', () => {
            window.setTimeout(completeHandoff, 0);
          }, { once: true });
          quickAddModal.hide(true);
        } else {
          await completeHandoff();
        }

        this.emitState('success');
      } catch (error) {
        console.error(error);

        const message = 'We could not add that item. Please try again.';
        this.handleErrorMessage(message);
        this.error = true;
        this.emitState('error', message);
      } finally {
        this.submitButton.classList.remove('loading');

        if (this.nativeCart?.classList.contains('is-empty')) {
          this.nativeCart.classList.remove('is-empty');
        }

        if (!this.error) {
          this.submitButton.removeAttribute('aria-disabled');
        }

        this.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
        this.emitState('complete');
      }
    }

    handleErrorMessage(errorMessage = false) {
      if (this.hideErrors) return;

      this.errorMessageWrapper =
        this.errorMessageWrapper ||
        this.querySelector('.product-form__error-message-wrapper');

      if (!this.errorMessageWrapper) return;

      this.errorMessage =
        this.errorMessage ||
        this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage && this.errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}
